// @flow
import { TickMarkOutlined, ArrowLeftUniversal } from '@duxton-web/icons';
import {
  Button,
  Form,
  Alert,
  Flex,
  Typography,
  Input,
  Divider,
} from '@duxton-web/ui';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ProgressiveContainer from 'components/container/ProgressiveContainer';
import {
  colorStatusPositiveDefault,
  colorContentPlaceholder,
} from '@duxton-web/design-tokens/themes/light';
import {
  CREATE_UCD_PROJECT,
  createUCDProject,
  GET_UCD_PROJECT,
  getUCDProject,
  UPDATE_UCD_PROJECT,
  updateUCDProject,
} from 'omega-rtc/actions/ucd';
import DataBox from 'omega-rtc/components/custom/DataBox';
import { snackBar } from 'omega-rtc/components/custom/SnackBar';
import ShowIfAuthorized from 'omega-rtc/components/form/ucd/common/ShowIfAuthorized';
import UCDProjectCodeLabel from 'omega-rtc/components/form/ucd/common/UCDProjectCodeLabel';
import {
  INTERNAL_STATUS,
  PROJECT_FORM_KEYS,
  UCD_ACTION_TYPES,
  UCD_RESOURCE_NAMES,
  UCD_ROUTES_LINK,
} from 'omega-rtc/constants/ucd';
import {
  generateProjectCode,
  getRequestStateKey,
} from 'omega-rtc/utils/ucd-utils';
import { hasJustSuccess, isRequestsPending } from 'utils/request-state-utils';
import './ProjectDetail.scss';
import ProjectStatusTag from './ProjectStatusTag';
import './UCDHomePage.scss';

import AudienceTypeSelector from 'omega-rtc/components/custom/entities/AudienceTypeSelector';
import BusinessObjectiveSelector from 'omega-rtc/components/custom/entities/BusinessObjectiveSelector';
import CountrySelector from 'omega-rtc/components/custom/entities/CountrySelector';

const ProjectEditPage = (props) => {
  const { router, location } = props;

  const searchParams = location.query;

  const duplicateID = searchParams.projectToDuplicate;
  const isDuplicate = !!duplicateID;

  const { projectID } = useMemo(() => {
    return {
      projectID: props.params.entityID,
    };
  }, [props.params]);

  const [form] = Form.useForm();

  const dispatch = useDispatch();

  const countries = useSelector((state) => state.countries.entities);
  const user = useSelector((state) => state.app.user);
  const projectView = useSelector((state) =>
    _.get(state, 'ucd.projectView', { project: {} }),
  );
  const createProjectRequest = useSelector((state) =>
    _.get(state.requests, getRequestStateKey(CREATE_UCD_PROJECT), {}),
  );
  const createProjectRequestRef = useRef();
  const updateProjectRequest = useSelector((state) =>
    _.get(state.requests, getRequestStateKey(UPDATE_UCD_PROJECT), {}),
  );
  const updateProjectRequestRef = useRef();
  const newProjectID = useSelector((state) => state.ucd.newProjectID);
  const getProjectRequest = useSelector((state) =>
    _.get(state.requests, getRequestStateKey(GET_UCD_PROJECT), {}),
  );

  useEffect(() => {
    if (projectView && projectView.project && isDuplicate) {
      form.setFieldValue(
        PROJECT_FORM_KEYS.NAME,
        `Copy of ${projectView.project.name}`,
      );
    }
  }, [projectView]);

  useEffect(() => {
    createProjectRequestRef.current = createProjectRequest;
    updateProjectRequestRef.current = updateProjectRequest;

    dispatch(getUCDProject(isDuplicate ? duplicateID : projectID));
  }, [projectID, location, dispatch]);

  const isCreating = isRequestsPending(createProjectRequest);

  useEffect(() => {
    if (isCreating) {
      createProjectRequestRef.current = createProjectRequest;
    }
  }, [isCreating, createProjectRequest]);

  const isUpdating = isRequestsPending(updateProjectRequest);
  useEffect(() => {
    if (isUpdating) {
      updateProjectRequestRef.current = updateProjectRequest;
    }
  }, [isUpdating, updateProjectRequest]);

  useEffect(() => {
    const { getFieldsValue } = form;
    const formValues = getFieldsValue();
    if (hasJustSuccess(createProjectRequestRef.current, createProjectRequest)) {
      snackBar.success({
        content: `${formValues.name} has been successfully created!`,
      });
      createProjectRequestRef.current = createProjectRequest;
      router.push(UCD_ROUTES_LINK.PROJECT_VIEW(newProjectID));
    }
    if (hasJustSuccess(updateProjectRequestRef.current, updateProjectRequest)) {
      snackBar.success({ content: 'Project has been successfully updated!' });
      updateProjectRequestRef.current = updateProjectRequest;
      router.push(UCD_ROUTES_LINK.PROJECT_VIEW(projectID));
    }
  }, [
    createProjectRequest,
    updateProjectRequest,
    form,
    router,
    newProjectID,
    projectID,
  ]);

  const handleSave = () => {
    const data = form.getFieldsValue();
    const { audience, objective, name, shortName, country } = data;

    const chosenCountry = countries.find(
      (globalCountry) => globalCountry.countryID === country,
    );

    const projectCode = generateProjectCode({
      countryCode: chosenCountry.code,
      year: new Date().getFullYear(),
      audience,
      objective,
      shortName,
    });

    const payload = {
      project: {
        audience,
        objective,
        name,
        shortName,
        code: projectCode,
        countryID: country,
        timezone: chosenCountry.timezones[0].tz,
      },
      status: isDuplicate ? INTERNAL_STATUS.draft : projectView.status,
      user: user.email,
    };
    try {
      if (isDuplicate) {
        dispatch(createUCDProject(payload));
        createProjectRequestRef.current = createProjectRequest;
      } else {
        dispatch(updateUCDProject(projectID, payload));
        updateProjectRequestRef.current = updateProjectRequest;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getProjectCode = (form, projectView) => {
    const { getFieldsValue } = form;
    const formValues = getFieldsValue();
    if (!_.isEmpty(formValues)) {
      const { country, audience, goal, projectCode } = formValues;
      if (country && audience && goal && projectCode) {
        return generateProjectCode({
          countryCode: _.get(_.find(countries, ['countryID', country]), 'code'),
          year: new Date().getFullYear(),
          audience,
          objective: goal,
          shortName: projectCode,
        });
      }
    }
    return _.get(projectView, 'project.code');
  };

  const isLoading = isRequestsPending(getProjectRequest) || !countries || !user;

  const { name, shortName, countryID, objective, audience } =
    projectView.project;
  const { status, campaignCount } = projectView;

  const code = isLoading ? '' : getProjectCode(form, projectView);

  const statusUpper = _.toUpper(status);
  const isProjectActive =
    INTERNAL_STATUS.active === statusUpper && !isDuplicate;
  const doesCampaignExist = campaignCount > 0 && !isDuplicate;
  const hasDuplicateCodeChanged = projectView.project.code !== code;
  const isProjectDeactivated =
    INTERNAL_STATUS.deactivated === statusUpper && !isDuplicate;

  return (
    <div className={'ucd-basic'}>
      <ProgressiveContainer isLoading={isLoading}>
        <Flex align="center" justify="flex-start" vertical={false} gap={8}>
          <Button
            type="text"
            onClick={() =>
              router.push(
                UCD_ROUTES_LINK.PROJECT_VIEW(
                  isDuplicate ? duplicateID : projectID,
                ),
              )
            }
          >
            <ArrowLeftUniversal style={{ fontSize: 20 }} />
            <Typography.Body level={3}>
              Back to project detail page
            </Typography.Body>
          </Button>
        </Flex>

        <Typography.Title level={3}>
          {isDuplicate ? 'Duplicate Project' : 'Edit Project'}
        </Typography.Title>

        <Form
          form={form}
          layout="vertical"
          style={isProjectDeactivated ? { cursor: 'not-allowed' } : {}}
        >
          <Flex vertical={false}>
            <Flex vertical style={{ width: '50%', paddingRight: 40 }} gap={8}>
              {isProjectActive && (
                <Alert
                  closable
                  message="Some information can't be edited as this project has been published."
                />
              )}
              <Typography.Title level={4}>Project Description</Typography.Title>
              <Form.Item
                name={PROJECT_FORM_KEYS.NAME}
                rules={[
                  {
                    required: true,
                    message: 'Please input your project description',
                  },
                ]}
                initialValue={isDuplicate ? `Copy of ${name}` : name}
              >
                <Input
                  disabled={isProjectDeactivated}
                  placeholder="Please input your project description"
                />
              </Form.Item>
              <Typography.Title level={4}>Project Details</Typography.Title>
              <Flex gap={16} vertical>
                <Flex vertical={false} gap={8} style={{ width: '100%' }}>
                  <Form.Item
                    className="sub-form-project-detail-item"
                    label="Audience"
                    style={{ flex: 1 }}
                    name={PROJECT_FORM_KEYS.AUDIENCE}
                    rules={[
                      {
                        required: true,
                        message: 'Please select your audience!',
                      },
                    ]}
                    initialValue={audience}
                  >
                    <AudienceTypeSelector
                      disabled={doesCampaignExist || isProjectDeactivated}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Goal"
                    style={{ flex: 1 }}
                    name={PROJECT_FORM_KEYS.OBJECTIVE}
                    rules={[
                      {
                        required: true,
                        message: 'Please select your Goal!',
                      },
                    ]}
                    initialValue={objective}
                  >
                    <BusinessObjectiveSelector
                      disabled={doesCampaignExist || isProjectDeactivated}
                    />
                  </Form.Item>
                </Flex>
                <Flex vertical={false} gap={8} style={{ width: '100%' }}>
                  <Form.Item
                    className="sub-form-project-detail-item"
                    style={{ flex: 1 }}
                    label="Project Name"
                    name={PROJECT_FORM_KEYS.SHORT_NAME}
                    rules={[
                      {
                        required: true,
                        message:
                          'Must be unique alphanumeric text, less than 10 characters.',
                        pattern: '^[a-zA-Z0-9]{1,10}$',
                      },
                    ]}
                    initialValue={isDuplicate ? null : shortName}
                  >
                    <Input
                      style={{ width: '100%' }}
                      disabled={isProjectActive || isProjectDeactivated}
                      placeholder="Please input your project name"
                    />
                  </Form.Item>
                  <Form.Item
                    className="sub-form-project-detail-item"
                    style={{ flex: 1 }}
                    label="Country"
                    name={PROJECT_FORM_KEYS.COUNTRY}
                    rules={[
                      {
                        required: true,
                        message: 'Please select your Country!',
                      },
                    ]}
                    initialValue={countryID}
                  >
                    <CountrySelector
                      disabled={doesCampaignExist || isProjectDeactivated}
                    />
                  </Form.Item>
                </Flex>
              </Flex>
              {isDuplicate && hasDuplicateCodeChanged && (
                <UCDProjectCodeLabel code={code} />
              )}
              {!isDuplicate && <UCDProjectCodeLabel code={code} />}
              <Flex vertical={false} justify="flex-end" align="center">
                <Button
                  disabled={isProjectDeactivated}
                  style={{
                    fontWeight: 'bold',
                    marginRight: 8,
                    padding: '10px 20px',
                  }}
                  type="default"
                  onClick={() =>
                    router.push(
                      UCD_ROUTES_LINK.PROJECT_VIEW(
                        isDuplicate ? duplicateID : projectID,
                      ),
                    )
                  }
                >
                  Cancel
                </Button>
                <ShowIfAuthorized
                  conditions={[
                    {
                      resource: UCD_RESOURCE_NAMES.project,
                      action: isDuplicate
                        ? UCD_ACTION_TYPES.create
                        : UCD_ACTION_TYPES.update,
                    },
                  ]}
                >
                  <Button
                    disabled={isProjectDeactivated}
                    style={{
                      fontWeight: 'bold',
                      padding: '10px 20px',
                    }}
                    type="primary"
                    loading={isRequestsPending(
                      createProjectRequest,
                      updateProjectRequest,
                    )}
                    onClick={handleSave}
                  >
                    Save
                  </Button>
                </ShowIfAuthorized>
              </Flex>
            </Flex>
            <Divider
              type="vertical"
              style={{
                height: 500,
              }}
            />
            {!isDuplicate && (
              <Flex vertical style={{ width: '50%' }} gap={16}>
                <Typography.Title level={4}>Project Status</Typography.Title>
                <DataBox
                  label="Status"
                  content={
                    <Flex vertical={false} align="center" justify="flex-start">
                      <ProjectStatusTag status={status} />
                      <TickMarkOutlined
                        style={{
                          color: _.get(projectView, 'isMetricsAvailable')
                            ? colorStatusPositiveDefault
                            : colorContentPlaceholder,
                          fontSize: 24,
                        }}
                      />
                    </Flex>
                  }
                />

                <DataBox label="Creator" content={projectView.createdBy} />
              </Flex>
            )}
          </Flex>
        </Form>
      </ProgressiveContainer>
    </div>
  );
};

const ProjectEditPageComponent = (props) => (
  <ShowIfAuthorized
    conditions={[
      {
        resource: UCD_RESOURCE_NAMES.project,
        action: UCD_ACTION_TYPES.create,
      },
      {
        resource: UCD_RESOURCE_NAMES.project,
        action: UCD_ACTION_TYPES.update,
      },
    ]}
    unauthorizedContent={
      <Alert
        message="You don't have edit access to Holos projects"
        showIcon
        type="error"
      />
    }
  >
    <ProjectEditPage {...props} />
  </ShowIfAuthorized>
);

export default ProjectEditPageComponent;
